import React, { useRef, useState } from 'react';
import { Avatar, Box, IconButton, CircularProgress, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import useUploadOrgLogo from '@/shared/hooks/org/useUploadOrgLogo';
import useDeleteOrgLogo from '@/shared/hooks/org/useDeleteOrgLogo';
import { getFirstLettersOfName } from '@/shared/services';

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,image/svg+xml';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

interface OrgLogoUploadProps {
  logoPath?: string | null;
  orgName: string;
  onSuccess?: () => void;
  onDelete?: () => void;
}

export const OrgLogoUpload: React.FC<OrgLogoUploadProps> = ({
  logoPath,
  orgName,
  onSuccess,
  onDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadOrgLogo();
  const deleteMutation = useDeleteOrgLogo();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isBusy = uploadMutation.isPending || deleteMutation.isPending;

  const initials = getFirstLettersOfName(orgName) ?? '?';

  const avatarSrc = previewUrl ?? logoPath ?? undefined;

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    if (file.size > MAX_FILE_SIZE) {
      setError('File must be under 5 MB');
      return;
    }

    // Show instant preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    try {
      await uploadMutation.mutateAsync(file);
      onSuccess?.();
    } catch {
      setError('Upload failed');
    } finally {
      setPreviewUrl(null);
      URL.revokeObjectURL(objectUrl);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteMutation.mutateAsync();
      onDelete?.();
    } catch {
      setError('Delete failed');
    }
  };

  const hasPicture = Boolean(avatarSrc);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Avatar
          src={avatarSrc}
          variant="rounded"
          sx={{ width: 80, height: 80, fontSize: 32, cursor: 'pointer' }}
          onClick={handleClick}
        >
          {initials}
        </Avatar>

        <IconButton
          size="small"
          onClick={handleClick}
          disabled={isBusy}
          sx={{
            position: 'absolute',
            bottom: -4,
            right: -4,
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
            width: 28,
            height: 28,
          }}
        >
          {uploadMutation.isPending ? (
            <CircularProgress size={14} />
          ) : (
            <PhotoCameraIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>

        {hasPicture && (
          <IconButton
            size="small"
            onClick={handleDelete}
            disabled={isBusy}
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              bgcolor: 'background.paper',
              border: '2px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'error.dark' },
              width: 28,
              height: 28,
            }}
          >
            {deleteMutation.isPending ? (
              <CircularProgress size={14} />
            ) : (
              <DeleteIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          hidden
          onChange={handleFileChange}
        />
      </Box>

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
};
